import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "motion/react"
import HistoryItem from "../../../../features/profile/HistoryItem"
import { useRequest } from "../../../../hooks/useRequest"
import { openAuth } from "../../../../lib/openAuth"
import { userStore } from "../../../../store/userStore"
import { OrderHistoryItem } from "../../../../types/entities/orderHistory"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export default function History() {
    const { request } = useRequest()
    const { user } = userStore()
    const navigate = useNavigate()
    const [items, setItems] = useState<OrderHistoryItem[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)

    const loadHistory = async (lastId?: string) => {
        const path = lastId ? `user/order/history?last_id=${lastId}` : "user/order/history"
        const data = await request<OrderHistoryItem[]>("GET", path)
        if (lastId) {
            setItems(prev => [...prev, ...data])
        } else {
            setItems(data)
        }
        setHasMore(data.length === 10)
        return data
    }

    useEffect(() => {
        if (!user) {
            openAuth(navigate, { pathname: "/profile" }, { replace: true })
            return
        }
        setLoading(true)
        loadHistory().finally(() => setLoading(false))
    }, [user])

    const loadMore = async () => {
        const lastItem = items[items.length - 1]
        if (!lastItem) return
        setLoadingMore(true)
        try {
            await loadHistory(lastItem.id)
        } finally {
            setLoadingMore(false)
        }
    }

    const canLoadMore = hasMore && items.length > 0

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <motion.div
                    className="size-10 rounded-full border-2 border-white/10 border-t-[#349be6]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
                />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            <h2
                className="text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: "-0.01em" }}>
                🕐 История покупок
            </h2>

            {items.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                    <span className="text-[56px]">📋</span>
                    <p className="text-white/40 text-[15px]" style={{ fontFamily: BODY }}>
                        История покупок пуста
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-3">
                        {items.map((item, i) => (
                            <HistoryItem key={`${item.id}-${item.number}`} item={item} index={i} />
                        ))}
                    </div>

                    {canLoadMore && (
                        <div className="flex justify-center mt-2">
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                type="button"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="rounded-full px-6 py-2.5 text-[13px] border border-white/15 bg-white/[0.06] text-white/60 hover:text-white hover:border-white/40 transition-all disabled:opacity-50"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                {loadingMore ? "Загружаю..." : "Показать ещё"}
                            </motion.button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
